<template>
  <div class="page-main">
    <div class="page-top">
      <div class="flex flex-y-center">
        <span class="title">库存模块流程</span>
        <span class="c-gray m-l-10">1 → 09 顺着走：库存可以单独用；要和销售、采购、生产联动，就在建单时选单接入</span>
      </div>
      <div class="flex flex-y-center">
        <el-button size="mini" :type="activeId === 'overview' ? 'primary' : ''" @click="activeId = 'overview'">
          流程全景
        </el-button>
      </div>
    </div>

    <div class="flow-wrap">
      <!-- 左侧：流程导航 -->
      <div class="flow-nav">
        <div class="nav-item" :class="{ active: activeId === 'overview' }" @click="activeId = 'overview'">
          <span class="nav-no">总</span>
          <span class="nav-text">流程全景图</span>
        </div>
        <div v-for="group in groups" :key="group.name" class="nav-group">
          <div class="nav-group-name">{{ group.name }}</div>
          <div
            v-for="item in group.items"
            :key="item.id"
            class="nav-item"
            :class="{ active: activeId === item.id }"
            @click="activeId = item.id"
          >
            <span class="nav-no">{{ item.no }}</span>
            <span class="nav-text">{{ item.title }}</span>
          </div>
        </div>
      </div>

      <!-- 右侧：流程全景 -->
      <div class="flow-body">
        <template v-if="activeId === 'overview'">
          <div class="card">
            <div class="card-title">四条线一起看（左边独立跑，右边接业务）</div>
            <div class="lane" v-for="lane in lanes" :key="lane.name">
              <div class="lane-name" :style="{ color: lane.color }">{{ lane.name }}</div>
              <div class="lane-nodes">
                <template v-for="(node, i) in lane.nodes">
                  <span v-if="i > 0" :key="'a' + i" class="arrow">→</span>
                  <span
                    :key="node.title + i"
                    class="node"
                    :style="{ borderLeftColor: lane.color }"
                    :title="'点击查看：' + node.title"
                    @click="activeId = node.stepId"
                  >
                    {{ node.title }}
                  </span>
                </template>
              </div>
            </div>
            <div class="lane-tip">
              点击任意单据方块查看那一步的操作说明；选中后右上角有【进入该页面】可直接跳过去。
            </div>
          </div>

          <div class="card m-t-10">
            <div class="card-title">三句话记住库存怎么用</div>
            <div class="key-line">
              <span class="key-no">1</span>
              <span>账只认一件事：<b>入库单、出库单审核</b>。单据不审核，库存永远不动。</span>
            </div>
            <div class="key-line">
              <span class="key-no">2</span>
              <span>接业务就"选单"：<b>采购订单→入库单</b>、<b>销售订单/送货单→出库单</b>；不接业务就手录。<b>两套账同一套逻辑</b>。</span>
            </div>
            <div class="key-line">
              <span class="key-no">3</span>
              <span>账实不符只走一条路：<b>盘点单</b>。盘盈盘亏自动调账并留痕，不要直接改库存数。</span>
            </div>
          </div>

          <div class="card m-t-10">
            <div class="card-title">独立使用能力自检（能不能不接销售/采购单独跑）</div>
            <el-table :data="checkRows" size="mini" border class="struct-table">
              <el-table-column prop="name" label="能力" width="190" />
              <el-table-column prop="now" label="系统当前怎么做" />
              <el-table-column label="结论" width="92" align="center">
                <template slot-scope="{ row }">
                  <span :class="'tag-' + row.level">{{ row.result }}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="card m-t-10">
            <div class="card-title">和线下台账对照（能不能替代 Excel）</div>
            <el-table :data="diffRows" size="mini" border class="struct-table">
              <el-table-column prop="offline" label="线下做法" width="210" />
              <el-table-column prop="online" label="系统当前怎么做" />
              <el-table-column label="结论" width="92" align="center">
                <template slot-scope="{ row }">
                  <span :class="'tag-' + row.level">{{ row.result }}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="card m-t-10 warn-card">
            <div class="card-title warn-title">三处最容易出错（仓库必须记住）</div>
            <div class="warn-line">① 单据只保存不审核 → 库存不动，第二天就"账实不符"。</div>
            <div class="warn-line">② 送货单/工单自动生成的只是<b>草稿</b>出入库单，仓库不补库位/批号并审核，账就不会动。</div>
            <div class="warn-line">③ 手录物料时材质、规格、批号写法不统一 → 同一种货被拆成两条库存，出库时报"库存不足"。</div>
          </div>
        </template>

        <!-- 右侧：单步说明 -->
        <template v-else-if="current">
          <div class="detail-head">
            <div class="flex flex-y-center">
              <span class="step-no">{{ current.no }}</span>
              <div>
                <div class="flex flex-y-center">
                  <span class="step-title">{{ current.title }}</span>
                  <span class="role-tag">{{ current.role }}</span>
                </div>
                <div class="step-summary">{{ current.summary }}</div>
              </div>
            </div>
            <el-button v-if="current.path" size="mini" type="primary" @click="openPage(current.path)">
              进入【{{ current.pathName }}】
            </el-button>
          </div>

          <!-- 常见问题 -->
          <div v-if="current.faqs" class="card m-t-10">
            <div class="card-title">常见问题速查</div>
            <div v-for="(qa, i) in current.faqs" :key="i" class="qa">
              <div class="qa-q">问：{{ qa.q }}</div>
              <div class="qa-a">答：{{ qa.a }}</div>
            </div>
          </div>

          <div v-else class="detail-grid">
            <div class="card">
              <div class="card-title">操作步骤</div>
              <ol class="ops">
                <li v-for="(s, i) in current.steps" :key="i">{{ s }}</li>
              </ol>
            </div>

            <div class="card">
              <div class="card-title">单据状态怎么走</div>
              <div class="status-flow">
                <span v-for="(st, i) in current.status" :key="i" class="status-item">
                  {{ st }}<span v-if="i < current.status.length - 1" class="status-arrow">›</span>
                </span>
              </div>

              <div class="card-title m-t-15">上游 / 下游</div>
              <div class="io">
                <span class="io-label">上游</span>
                <span class="io-value">{{ current.up }}</span>
              </div>
              <div class="io">
                <span class="io-label">下游</span>
                <span class="io-value">{{ current.down }}</span>
              </div>

              <template v-if="current.warn && current.warn.length">
                <div class="card-title m-t-15">注意</div>
                <div v-for="(w, i) in current.warn" :key="i" class="warn-line">{{ w }}</div>
              </template>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
// 库存模块说明数据：按现有菜单、按钮、后端实际校验口径整理
const STEPS = [
  {
    id: 'master',
    no: '01',
    group: '库存主线（可独立使用）',
    title: '仓库与仓位（基础数据）',
    role: '仓库主管',
    path: '/new/stock/warehouse/list',
    pathName: '仓库',
    summary: '库存的所有单据都挂在「仓库 + 仓位」上，先把仓库、仓位建好，入出库单才选得到库位。',
    steps: [
      '进入【仓库】，新增仓库，填名称、负责人、地址；一个物理仓建一条。',
      '进入【仓位】，按仓库建仓位（货架/区域/料位），名称建议“区-架-层”。',
      '【仓库平面图】把仓位摆到图上，货在哪一目了然（只影响展示，不影响账）。',
      '仓库不用了就停用，不要删；已经有单据的仓库删不掉。'
    ],
    status: ['新增', '启用', '停用（不再可选）'],
    up: '无（基础数据）',
    down: '入库单、出库单、盘点单、调拨单 都靠它选库位',
    warn: [
      '只建仓库不建仓位：以后想按仓位查货、按仓位盘点都做不了，只能整仓一起盘。',
      '停机/停用的仓位不要复用，历史流水会跟着乱。'
    ]
  },
  {
    id: 'in',
    no: '02',
    group: '库存主线（可独立使用）',
    title: '入库单（货进仓）',
    role: '仓管员',
    path: '/new/stock/stockIn/list',
    pathName: '入库单',
    summary: '货进仓就做入库单。入库单【审核】之后库存才增加、流水才生成。来源可以是采购单，也可以完全手录。',
    steps: [
      '【入库单】→【新增入库单】→ 选仓库、仓位、入库日期。',
      '接采购：点【从采购单选择】，勾采购订单明细，系统自动带出物料、数量、单价、供应商；可入库量 = 下单数 − 已入数。',
      '不接采购（独立用）：来源类型选「手动入库」，点【增行】手录 物料名称 / 材质 / 规格 / 单位 / 数量 / 单价 / 批号。',
      '来源类型要选对：采购入库、送货入库、退货入库、生产工单入库、委外工单入库、手动入库。',
      '点【审核】：库存主档增加、写一条入库流水；录错点【反审核】回滚。'
    ],
    status: ['草稿', '待审核', '已审核（库存增加）', '作废（仅未审核）'],
    up: '采购订单（可选）；生产/委外工单完工收货（自动生成草稿）；手工',
    down: '库存台账、库存流水、收发存汇总',
    warn: [
      '只保存不审核 = 没入库，仓库最容易犯的错。',
      '生产/委外完工生成的只是【草稿】入库单，必须到入库单里补仓位、批号再审核，否则成品不在库存里。',
      '手录物料时材质、规格、批号必须按公司统一写法，写错一个字就会被当成另一种货。'
    ]
  },
  {
    id: 'stock',
    no: '03',
    group: '库存主线（可独立使用）',
    title: '库存台账与流水（查账）',
    role: '仓管 / 销售 / 采购',
    path: '/new/stock/inventory/list',
    pathName: '库存列表',
    summary: '库存列表回答“现在有多少”，点进去的流水回答“怎么变成这么多的”。任何对不上，都先查流水。',
    steps: [
      '【库存列表】按仓库、仓位、材质、规格、批号、物料名称筛选。',
      '点每行【流水】：时间、单据号、前数、变动数、后数、操作人，一笔一笔都在。',
      '流水里的“业务类型”能直接看出是谁造成的：入库、出库、调拨出入、盘点盈亏。',
      '库存数只能由单据审核产生，系统不提供直接改数入口。'
    ],
    status: ['无单据状态（实时结果）'],
    up: '入库单、出库单、调拨单、盘点单（都是审核后才生效）',
    down: '收发存汇总、财务库存对账',
    warn: [
      '库存不对不要改数，改数就没有痕迹；正确做法是开盘点单，让系统记一笔盘盈/盘亏流水。',
      '库存主档的唯一维度是「仓库 + 仓位 + 材质 + 规格 + 批号」，物料名称只是显示用，不参与匹配。'
    ]
  },
  {
    id: 'out',
    no: '04',
    group: '库存主线（可独立使用）',
    title: '出库单（货出仓）',
    role: '仓管员',
    path: '/new/stock/stockOut/list',
    pathName: '出库单',
    summary: '货出仓做出库单，审核时校验库存够不够，不够直接不让出。发货可以选销售订单，也可以完全手录。',
    steps: [
      '【出库单】→【新增出库单】→ 选仓库、仓位、出库日期、客户。',
      '接销售：点【选择销售订单明细】勾选后，系统自动带出客户、商品、数量、单价。',
      '送货单已审核的：系统会自动生成一张【草稿】出库单（来源=送货出库），到这里补仓位/批号再审核。',
      '不接销售（独立用）：来源类型选「手动出库」或「领料出库」，点【增行】手录明细。',
      '点【审核】：按 仓库+仓位+材质+规格+批号 扣库存；不足会提示“现有 X，本次需出库 Y”。'
    ],
    status: ['草稿', '待审核', '已审核（库存扣减）', '作废'],
    up: '销售订单（可选）；送货单审核（自动草稿）；手工出库 / 领料',
    down: '库存台账、库存流水、收发存汇总',
    warn: [
      '出库单不审核，库存不会减，“账上有货实际没货”就是这么来的。',
      '报“库存不足”通常不是系统错，是当初入库时仓位或规格写法不一致，先去查流水核对。',
      '同一张送货单不要人工再建一张出库单，否则重复扣库存。'
    ]
  },
  {
    id: 'check',
    no: '05',
    group: '库存主线（可独立使用）',
    title: '盘点单（账实校准）',
    role: '仓管 + 财务监盘',
    path: '/new/stock/stockCheck/list',
    pathName: '盘点单',
    summary: '盘点就是把账面数改成实际数。系统按“实盘 − 账面”自动算盈亏，一次审核完成调账并留痕。',
    steps: [
      '【盘点单】→【新增】→ 选盘点仓库、盘点日期。',
      '点【从库存生成盘点明细】，整仓账面一次带出；也可以【增行】只盘某几行。',
      '打印出去盘点，把实盘数录到“实盘数”列；实盘数留空视为与账面一致。',
      '盘点期间有出入库时，点【重取账面】刷新账面（实盘数保留）。',
      '提交 → 审核：按最新账面重算差异，盘盈加库存、盘亏减库存，记“盘点盘盈／盘点盘亏”流水。'
    ],
    status: ['草稿', '待审核', '已审核（库存按差异调整）', '作废'],
    up: '库存台账（账面数）',
    down: '库存台账、库存流水、收发存汇总',
    warn: [
      '审核才生效。审核前尽量别再审别的库存单据，否则差异算的是旧账面。',
      '差异不单独生成“盘盈单/盘亏单”，直接体现在盘点单和流水里，查账就看盘点单。',
      '审核错了可以【反审核】，系统会按已保存的差异反向回滚库存。'
    ]
  },
  {
    id: 'transfer',
    no: '06',
    group: '库存主线（可独立使用）',
    title: '调拨单（仓间移位）',
    role: '仓管员',
    path: '/new/stock/stockTransfer/list',
    pathName: '调拨单',
    summary: '仓与仓、仓位与仓位之间倒料用调拨单，一次审核同时完成“调出扣减 + 调入增加”。',
    steps: [
      '【调拨单】→【新增】→ 选调出仓库/仓位、调入仓库/仓位。',
      '点【选择库存】把要调的料带出来，填调拨数量。',
      '数量必须大于 0；调出与调入的仓库/仓位/批号不能完全相同，系统会拦。',
      '点【审核】：调出侧不足会直接失败并提示；成功后生成两条流水（调拨出库、调拨入库）。'
    ],
    status: ['草稿', '待审核', '已审核（双向记账）', '作废'],
    up: '库存台账',
    down: '库存台账（两边同时变）、库存流水',
    warn: [
      '车间跨区挪料也要走调拨单，不然一个仓多、一个仓少，月底怎么都对不上。',
      '【反审核】会把调出、调入两边一起回滚。'
    ]
  },
  {
    id: 'sfc',
    no: '07',
    group: '库存主线（可独立使用）',
    title: '收发存汇总（月结对账）',
    role: '仓管 / 财务',
    path: '/new/stock/inventory/sfc',
    pathName: '收发存汇总',
    summary: '按期初 + 本期入 − 本期出 = 期末 对账，这是仓库交给财务的月度报表。',
    steps: [
      '【收发存汇总】选日期区间，一般选月初到月末。',
      '系统按「仓库 + 物料 + 材质 + 规格 + 批号」一行，算出期初、本期入库、本期出库、期末。',
      '和库存列表的期末数对一下：不一致多半是区间内有单据被反审核或跨期。',
      '导出给财务做库存对账。'
    ],
    status: ['无状态（统计报表）'],
    up: '库存流水',
    down: '财务库存对账',
    warn: [
      '目前只有数量，没有金额；要和财务对“库存资金占用”，得等金额补齐（见 09）。',
      '月结后尽量不要再反审核上月单据，否则上月报表会变数。'
    ]
  },
  {
    id: 'access',
    no: '08',
    group: '与业务对接',
    title: '三种接入方式（采购 / 销售 / 生产）',
    role: '仓管 / 各部门',
    summary: '库存可以完全独立跑；要和业务联动，就是“建单时选单”或“由上游自动生成草稿”，两种方式共用同一套库存逻辑。',
    steps: [
      '采购接入：采购订单审核 → 入库单【从采购单选择】→ 审核。数量受“下单数 − 已入数”限制，不会超入。',
      '销售接入（两种都行）：① 出库单【选择销售订单明细】手工建；② 送货单审核后自动生成草稿出库单，仓库只补仓位/批号。',
      '生产接入：生产/委外工单点【完工收货入库】→ 自动生成草稿入库单 → 仓库补仓位/批号后审核。',
      '独立使用：以上都不用，来源类型选“手动入库/手动出库/领料出库”，手录明细，账一样准。',
      '关键差别：自动生成的单据都是【草稿】，必须仓库审核才动库存——这样业务单据审核不会把库存改乱。'
    ],
    status: ['见 02～07 各单据状态'],
    up: '销售订单、送货单、采购订单、生产/委外工单',
    down: '库存台账（统一入口，不区分来源）',
    warn: [
      '送货单、工单没关联到明细行时不会自动生成，系统会提示“请手动创建入库单/出库单”，这时走手动建单，别卡在那儿。',
      '自动生成的单据有防重：同一来源单据只会生成一张，不会重复扣或重复加库存。'
    ]
  },
  {
    id: 'gap',
    no: '09',
    group: '与业务对接',
    title: '与线下的差异和待补',
    role: '所有人',
    summary: '现在的库存在“数量账”上已经能替代线下 Excel；在“金额账、货权、库龄”上还缺，这也是线下台账常做、系统没做的部分。',
    steps: [
      '已经能替代线下的：整仓盘点、批号管理、仓间调拨、出入库审批、流水追溯、收发存数量表。',
      '还缺、需要补的：库存金额与收发存金额、安全库存预警、库龄与呆滞料、货权（客户来料加工 / 供应商寄存）、盘点差异单及附件。',
      '建议补齐顺序：先补库存金额（财务对账必须要）→ 再补安全库存预警（采购、销售看货要用）→ 再做库龄与呆滞料。',
      '过渡办法：金额暂时用“数量 × 最近入库单价”手工算；客户来料单独建一个“来料仓”隔离，不进自有库存。'
    ],
    status: ['—（现状说明）'],
    up: '本页 01～08 的实际能力',
    down: '需求排期、财务存货核算',
    warn: [
      '库存流水表和库存主档都没有单价/金额字段，收发存目前出不了金额，这是最该先补的一项。',
      '没有货权字段，来料加工的料会和自有库存混在一起，算存货金额时会虚高。'
    ]
  },
  {
    id: 'faq',
    no: '10',
    group: '与业务对接',
    title: '常见问题速查',
    role: '所有人',
    summary: '仓库最常问的几个问题，遇到卡住先看这里。',
    faqs: [
      {
        q: '库存模块能不能不接销售、采购单独用？',
        a: '能。入库单来源选“手动入库”、出库单来源选“手动出库/领料出库”，手录物料明细；库存、盘点、调拨、收发存全部照常跑。接业务只是多一个“选单”的入口。'
      },
      {
        q: '入库/出库单审核了，库存没变？',
        a: '先确认审核成功（状态要变成“已审核”）；再看单据的仓库、仓位填没填；最后查这张单的流水有没有生成，没流水就是没真正审核。'
      },
      {
        q: '出库提示“库存不足”，但仓库里明明有货？',
        a: '系统按「仓库+仓位+材质+规格+批号」五要素匹配。去库存列表按同样条件查一下：多半是仓位不同、规格写法不同，或批号录错。'
      },
      {
        q: '盘点一定要停业吗？',
        a: '不用。可以先“从库存生成盘点明细”再盘点，盘完点【重取账面】把盘点期间的出入库算进去，审核时按最新账面重算差异。'
      },
      {
        q: '送货单/工单说生成了出库单/入库单，列表里找不到？',
        a: '生成的是草稿，按状态筛“草稿”就能看到；也可能是同一来源已生成过（系统防重）。草稿必须补仓位/批号并审核才会动库存。'
      },
      {
        q: '收发存为什么没有金额？',
        a: '当前库存流水、库存主档都没有单价字段，只能出数量。要和财务对金额，需要先补金额口径，见第 09 步。'
      }
    ]
  }
]

// 流程全景四条线
const LANES = [
  {
    name: '独立线（不接业务）',
    color: '#396bf6',
    nodes: [
      { title: '仓库/仓位', stepId: 'master' },
      { title: '入库单（手动）', stepId: 'in' },
      { title: '库存台账', stepId: 'stock' },
      { title: '出库单（手动/领料）', stepId: 'out' },
      { title: '盘点/调拨', stepId: 'check' },
      { title: '收发存汇总', stepId: 'sfc' }
    ]
  },
  {
    name: '采购接入',
    color: '#46bd87',
    nodes: [
      { title: '采购订单', stepId: 'access' },
      { title: '入库单（选采购单）', stepId: 'in' },
      { title: '审核入账', stepId: 'in' },
      { title: '库存台账', stepId: 'stock' }
    ]
  },
  {
    name: '销售接入',
    color: '#e6a23c',
    nodes: [
      { title: '销售订单', stepId: 'access' },
      { title: '出库单（选销售单）', stepId: 'out' },
      { title: '审核扣减', stepId: 'out' },
      { title: '库存台账', stepId: 'stock' }
    ]
  },
  {
    name: '生产/送货接入',
    color: '#909399',
    nodes: [
      { title: '生产/委外工单完工', stepId: 'access' },
      { title: '草稿入库单', stepId: 'in' },
      { title: '送货单审核', stepId: 'access' },
      { title: '草稿出库单', stepId: 'out' },
      { title: '补库位批号→审核', stepId: 'out' }
    ]
  }
]

// 独立使用能力自检（按后端实际校验整理）
const CHECK_ROWS = [
  { name: '不接采购单也能入库', now: '来源类型选“手动入库”，【增行】手录物料/材质/规格/数量/单价/批号', result: '支持', level: 'ok' },
  { name: '不接销售单也能出库', now: '来源类型选“手动出库/领料出库”，同样可手录明细', result: '支持', level: 'ok' },
  { name: '采购入库接入', now: '入库单【从采购单选择】，自动带供应商与来源单号，可入库量受“下单数−已入数”限制', result: '支持', level: 'ok' },
  { name: '销售出库接入', now: '出库单【选择销售订单明细】，自动带客户与商品', result: '支持', level: 'ok' },
  { name: '送货单联动出库', now: '送货单审核后自动生成草稿出库单，仓库补仓位/批号后审核才扣库存', result: '支持', level: 'ok' },
  { name: '生产/委外完工联动入库', now: '完工收货自动生成草稿入库单；工单未关联核算单明细行时会提示手动建单', result: '支持', level: 'ok' },
  { name: '库存不足拦截', now: '出库审核时按五要素校验，不足直接拒绝并提示“现有 X、需出 Y”', result: '支持', level: 'ok' },
  { name: '整仓盘点', now: '盘点单【从库存生成盘点明细】按仓库整仓带出，支持【重取账面】', result: '支持', level: 'ok' },
  { name: '反审核回滚', now: '入库/出库/调拨/盘点反审核都会反向写回库存并记流水', result: '支持', level: 'ok' },
  { name: '并发与重号防护', now: '库存主档乐观锁 + 单据号事务咨询锁，同一批货不会算错', result: '支持', level: 'ok' },
  { name: '库存金额 / 收发存金额', now: '流水表与库存主档都没有单价、金额字段，收发存只出数量', result: '缺', level: 'no' },
  { name: '安全库存预警 / 库龄', now: '无最低库存字段、无库龄计算与呆滞料清单', result: '缺', level: 'no' },
  { name: '货权区分（自有/来料/寄存）', now: '无货权字段，客户来料加工只能另建仓库隔离', result: '缺', level: 'no' }
]

// 与线下台账对照
const DIFF_ROWS = [
  { offline: 'Excel 台账按 材质+规格+批号 一行', online: '库存主档唯一维度＝仓库+仓位+材质+规格+批号', result: '一致', level: 'ok' },
  { offline: '一张入库单对应供应商一张送货单', online: '入库单可从采购单选择，也可手录；来源单号留痕', result: '一致', level: 'ok' },
  { offline: '抄磅单：记实际重量和数量', online: '明细有“数量 + 重量”两个字段，可同时记', result: '一致', level: 'ok' },
  { offline: '月末全仓盘点，盘盈盘亏写调账表', online: '盘点单整仓带出 → 录实盘 → 审核一次调账，差异进流水', result: '一致', level: 'ok' },
  { offline: '仓间倒料要两人签字', online: '调拨单一次审核完成调出扣减＋调入增加，可反审核', result: '半兼容', level: 'half' },
  { offline: '月底给财务一张收发存表（含金额）', online: '收发存汇总只有数量，没有金额列', result: '缺金额', level: 'no' },
  { offline: '客户来料加工的料不算自己的库存', online: '没有货权字段，来料只能另建仓库隔离', result: '缺货权', level: 'no' },
  { offline: '呆滞料、超期料清单', online: '无库龄、无预警', result: '缺库龄', level: 'no' }
]

export default {
  data() {
    return {
      activeId: 'overview',
      steps: STEPS,
      lanes: LANES,
      checkRows: CHECK_ROWS,
      diffRows: DIFF_ROWS
    }
  },
  computed: {
    groups() {
      const result = []
      this.steps.forEach((item) => {
        let group = result.find((g) => g.name === item.group)
        if (!group) {
          group = { name: item.group, items: [] }
          result.push(group)
        }
        group.items.push(item)
      })
      return result
    },
    current() {
      return this.steps.find((item) => item.id === this.activeId) || null
    }
  },
  watch: {
    activeId(val) {
      try {
        localStorage.setItem('stock_flow_active_id', val)
      } catch (e) {
        /* ignore */
      }
    }
  },
  mounted() {
    try {
      const last = localStorage.getItem('stock_flow_active_id')
      if (last === 'overview' || this.steps.some((item) => item.id === last)) this.activeId = last
    } catch (e) {
      /* ignore */
    }
  },
  methods: {
    // 说明书性质：新开页签打开，不打断当前作业
    openPage(path) {
      window.open(path, '_blank')
    }
  }
}
</script>

<style lang="less" scoped>
.page-main {
  padding-bottom: 10px;
  .page-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    .title {
      font-size: 15px;
      font-weight: bold;
    }
  }
}

.flow-wrap {
  display: flex;
  height: calc(100vh - 140px);
  min-height: 460px;
}

/* 左侧导航 */
.flow-nav {
  width: 216px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow-y: auto;
  padding: 6px 0 10px;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #dcdfe6;
    border-radius: 3px;
  }
  .nav-group-name {
    padding: 12px 14px 4px;
    font-size: 12px;
    color: #909399;
  }
  .nav-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    font-size: 13px;
    color: #303133;
    border-left: 3px solid transparent;
    cursor: pointer;
    &:hover {
      background: #f5f7fa;
    }
    &.active {
      background: rgba(57, 107, 246, 0.08);
      border-left-color: #396bf6;
      color: #396bf6;
      font-weight: bold;
    }
    .nav-no {
      width: 24px;
      flex-shrink: 0;
      font-size: 12px;
      color: #909399;
    }
    .nav-text {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
}

/* 右侧内容 */
.flow-body {
  flex: 1;
  min-width: 0;
  margin-left: 10px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c0c4cc;
    border-radius: 4px;
  }
}

.card {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 12px 14px;
  .card-title {
    font-size: 13px;
    font-weight: bold;
    padding-left: 8px;
    margin-bottom: 10px;
    border-left: 3px solid #396bf6;
    line-height: 1;
  }
}

/* 对照表 */
.struct-table {
  font-size: 12px;
  /deep/ th {
    background: #fafafa;
    padding: 6px 0;
  }
  /deep/ td {
    padding: 6px 0;
    line-height: 1.7;
  }
  .tag-ok {
    color: #46bd87;
    font-weight: bold;
  }
  .tag-half {
    color: #e6a23c;
    font-weight: bold;
  }
  .tag-no {
    color: #f56c6c;
    font-weight: bold;
  }
}

/* 全景图 */
.lane {
  display: flex;
  align-items: flex-start;
  padding: 8px 0;
  border-bottom: 1px dashed #ebeef5;
  &:last-of-type {
    border-bottom: none;
  }
  .lane-name {
    width: 118px;
    flex-shrink: 0;
    font-size: 12px;
    font-weight: bold;
    line-height: 30px;
  }
  .lane-nodes {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  .node {
    display: inline-block;
    padding: 6px 8px;
    margin: 3px 0;
    text-align: center;
    font-size: 12px;
    color: #303133;
    background: #fafafa;
    border: 1px solid #dcdfe6;
    border-left: 3px solid #909399;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
      background: #fff;
      border-color: #396bf6;
      color: #396bf6;
      box-shadow: 0 1px 6px rgba(57, 107, 246, 0.25);
    }
  }
  .arrow {
    color: #c0c4cc;
    margin: 0 4px;
    font-size: 12px;
  }
}
.lane-tip {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed #ebeef5;
  font-size: 12px;
  color: #909399;
}

.key-line {
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  color: #606266;
  line-height: 2;
  b {
    color: #396bf6;
  }
  .key-no {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin: 4px 8px 0 0;
    border-radius: 50%;
    background: #396bf6;
    color: #fff;
    font-size: 12px;
    text-align: center;
    line-height: 18px;
  }
}

.warn-card {
  background: #fffbf5;
  border-color: #f5dab1;
  .warn-title {
    border-left-color: #e6a23c;
    color: #b8741a;
  }
}
.warn-line {
  font-size: 12px;
  color: #b8741a;
  line-height: 1.9;
}

/* 单步说明 */
.detail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 12px 14px;
  .step-no {
    width: 34px;
    height: 34px;
    flex-shrink: 0;
    margin-right: 10px;
    border-radius: 50%;
    background: #396bf6;
    color: #fff;
    font-size: 15px;
    font-weight: bold;
    text-align: center;
    line-height: 34px;
  }
  .step-title {
    font-size: 15px;
    font-weight: bold;
  }
  .role-tag {
    margin-left: 8px;
    padding: 1px 8px;
    font-size: 12px;
    color: #46bd87;
    background: rgba(70, 189, 135, 0.12);
    border: 1px solid rgba(70, 189, 135, 0.35);
    border-radius: 10px;
  }
  .step-summary {
    margin-top: 4px;
    font-size: 12px;
    color: #909399;
    line-height: 1.7;
  }
}

.detail-grid {
  display: flex;
  align-items: flex-start;
  margin-top: 10px;
  .card {
    &:first-child {
      flex: 1.15;
      margin-right: 10px;
    }
    &:last-child {
      flex: 1;
    }
  }
}

// 窄屏上下排，避免两列挤成一团
@media screen and (max-width: 1280px) {
  .detail-grid {
    flex-direction: column;
    .card {
      width: 100%;
      &:first-child {
        flex: none;
        margin-right: 0;
        margin-bottom: 10px;
      }
      &:last-child {
        flex: none;
      }
    }
  }
}

.ops {
  margin: 0;
  padding-left: 18px;
  li {
    font-size: 13px;
    color: #606266;
    line-height: 2;
    margin-bottom: 2px;
    &::marker {
      color: #396bf6;
      font-weight: bold;
    }
  }
}

.status-flow {
  .status-item {
    font-size: 12px;
    color: #303133;
    background: #f4f6fa;
    border-radius: 3px;
    padding: 3px 8px;
    display: inline-block;
    margin-bottom: 4px;
  }
  .status-arrow {
    color: #909399;
    margin: 0 4px;
  }
}

.io {
  display: flex;
  align-items: flex-start;
  font-size: 12px;
  line-height: 1.9;
  & + .io {
    margin-top: 4px;
  }
  .io-label {
    width: 40px;
    flex-shrink: 0;
    color: #909399;
  }
  .io-value {
    flex: 1;
    min-width: 0;
    color: #606266;
  }
}

.qa {
  padding: 8px 0;
  border-bottom: 1px dashed #ebeef5;
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  .qa-q {
    font-size: 13px;
    font-weight: bold;
    color: #303133;
    line-height: 1.8;
  }
  .qa-a {
    font-size: 12px;
    color: #606266;
    line-height: 1.9;
    margin-top: 2px;
  }
}
</style>
